import { useState, useRef, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';

export const useMicrophone = (
  userName: string,
  onSpeaking: (who: string | null) => void,
  userMuted: boolean,
  blockMic: boolean
) => {
  const [micEnabled, setMicEnabled] = useState<boolean>(false);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<AudioWorkletNode | null>(null);

  const micActiveRef = useRef(false);
  const micShutdownRef = useRef(false);

  useEffect(() => { micActiveRef.current = micEnabled; }, [micEnabled]);

  const toggleMicEnabled = useCallback((enabled: boolean) => {
    if (enabled && (userMuted || blockMic)) return;
    setMicEnabled(enabled);
  }, [userMuted, blockMic]);

  const handleMicButton = async () => {
    if (blockMic) return;
    if (micEnabled) {
      toggleMicEnabled(false);
      return;
    }
    try {
      const unlockCtx = new (globalThis.window.AudioContext || (globalThis.window as any).webkitAudioContext)();
      const buffer = unlockCtx.createBuffer(1, 1, 22050);
      const source = unlockCtx.createBufferSource();
      source.buffer = buffer;
      source.connect(unlockCtx.destination);
      source.start(0);
      unlockCtx.close();
    } catch {}
    micShutdownRef.current = false;
    toggleMicEnabled(true);
  };

  useEffect(() => {
    if (!micEnabled) return;
    let stopped = false;
    micShutdownRef.current = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const audioContext = new (globalThis.window.AudioContext || (globalThis.window as any).webkitAudioContext)({ sampleRate: 16000 });
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        await audioContext.audioWorklet.addModule('/pcm-processor.js');
        const audioWorkletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
        processorRef.current = audioWorkletNode;
        audioWorkletNode.port.onmessage = (event: MessageEvent) => {
          if (stopped || micShutdownRef.current) return;
          if (!micActiveRef.current) return;
          const { type, samples } = event.data as { type: string; samples?: Float32Array };
          if (type === 'loud') {
            onSpeaking(userName);
          }
          if (type === 'audio' && samples) {
            let pcm = new Int16Array(samples.length);
            for (let i = 0; i < samples.length; i++) {
              let s = Math.max(-1, Math.min(1, samples[i]));
              pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
            }
            socketService.socket?.emit('audio_chunk', pcm.buffer);
          }
        };
        source.connect(audioWorkletNode).connect(audioContext.destination);
        socketService.socket?.emit('start_recognition');
      } catch (err) {
        console.log(`useMicrophone: Could not access microphone - ${err}`);
        toggleMicEnabled(false);
        onSpeaking(null);
        alert("Could not access microphone.");
      }
    })();

    return () => {
      stopped = true;
      micShutdownRef.current = true;
      if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
      if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
      if (streamRef.current) { 
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
        streamRef.current = null; 
      }
      socketService.socket?.emit('stop_recognition');
      onSpeaking(null);
      micActiveRef.current = false;
    };
  }, [micEnabled, userName, onSpeaking]);

  useEffect(() => {
    if (userMuted && micEnabled) {
      toggleMicEnabled(false);
    }
  }, [userMuted, micEnabled, toggleMicEnabled]);

  return {
    micEnabled,
    toggleMicEnabled,
    handleMicButton,
  };
};